import type { KeyboardEvent } from 'react'

/**
 * Arrow-key handler for a custom radiogroup built from `<button role="radio">`
 * options (the DS2026 radio-card pattern used by the import destination picker
 * and the CRM-duplicate mode selector).
 *
 * Implements the WAI-ARIA radio-group keyboard contract: Arrow Right/Down move
 * to the next option, Arrow Left/Up to the previous, both wrapping around and
 * skipping disabled options. Selection follows focus, so it also moves DOM
 * focus to the newly-selected radio (roving-tabindex: only the checked option
 * is in the tab order, so without this the focus would be stranded).
 *
 * Pair with `role="radiogroup"` + `aria-label` on the container and
 * `role="radio"`, `aria-checked`, and `tabIndex={checked ? 0 : -1}` on each
 * option button.
 */
export function radioGroupKeyDown<T extends string>(
  e: KeyboardEvent<HTMLButtonElement>,
  options: { value: T; disabled?: boolean }[],
  selected: T,
  onSelect: (value: T) => void
): void {
  const forward = e.key === 'ArrowRight' || e.key === 'ArrowDown'
  const backward = e.key === 'ArrowLeft' || e.key === 'ArrowUp'
  if (!forward && !backward) return
  e.preventDefault()

  const enabled = options.filter((o) => !o.disabled)
  if (enabled.length === 0) return

  const current = Math.max(
    0,
    enabled.findIndex((o) => o.value === selected)
  )
  const nextIdx =
    (current + (forward ? 1 : -1) + enabled.length) % enabled.length
  onSelect(enabled[nextIdx].value)

  // Move focus to the newly-selected radio (selection follows focus).
  const group = e.currentTarget.closest('[role="radiogroup"]')
  const radios = group?.querySelectorAll<HTMLButtonElement>(
    '[role="radio"]:not([disabled])'
  )
  radios?.[nextIdx]?.focus()
}
